<div class="wizard-wrapper">
    <div *ngIf="campaign" class="wizard">

        <!-- ==== Sidebar navigation ==== -->
        <div class="sidebar">
            <div class="campaign-title">{{campaign.name || 'New Campaign'}}</div>
            <div class="campaign-type">{{campaignTypeLabel()}}<span *ngIf="readOnly" class="view-only-badge">view only</span></div>

            <div class="nav-sections">
                <div *ngFor="let key of visibleSections()" class="nav-section"
                    [class.active]="campaign.activeStep == key"
                    [class.static]="readOnly"
                    (click)="goToStep(key)">
                    <span class="status"
                        [class.complete]="isSectionComplete(key)"
                        [class.restriction]="isOptionalSection(key)">
                        <ng-container *ngIf="isSectionComplete(key)">&#10003;</ng-container>
                        <ng-container *ngIf="!isSectionComplete(key)">{{isOptionalSection(key) ? '+' : '&#9679;'}}</ng-container>
                    </span>
                    <span class="section-text">
                        <span class="section-name">{{sections[key].name}}</span>
                        <span class="section-summary">{{sectionSummaryShort(key)}}</span>
                    </span>
                    <span *ngIf="isOptionalSection(key) && !readOnly" class="remove" title="Remove restriction"
                        (click)="removeSection(key); $event.stopPropagation()">&times;</span>
                </div>
            </div>

            <div *ngIf="!readOnly && availableRestrictions().length" class="add-restriction">
                <div class="add-button" (click)="addRestrictionOpen = !addRestrictionOpen">+ Add Restriction</div>
                <div *ngIf="addRestrictionOpen" class="restriction-menu">
                    <div *ngFor="let section of availableRestrictions()" class="restriction-option" (click)="addSection(section.key)">
                        <div class="name">{{section.name}}</div>
                        <div class="description">{{section.description}}</div>
                    </div>
                </div>
            </div>

            <div class="sidebar-footer">
                <ng-container *ngIf="!readOnly">
                    <div class="live-row" [class.not-ready]="!campaign.live && !readyToGoLive()">
                        <span>Live</span>
                        <app-toggle (valueChanged)="toggleLive()" [checked]="campaign.live"></app-toggle>
                    </div>
                    <div *ngIf="!campaign.live && !readyToGoLive()" class="ready-hint">Complete the marked sections before going live.</div>
                </ng-container>
                <div *ngIf="readOnly" class="live-row status-row" [class.is-active]="activeNow">
                    <span>Status</span>
                    <span class="status-text">{{statusLabel()}}</span>
                </div>
            </div>
        </div>

        <!-- ==== Active section ==== -->
        <div class="step">
            <div class="step-header">
                <div>{{sections[campaign.activeStep]?.name}}</div>
                <div *ngIf="isOptionalSection(campaign.activeStep)" class="header-note">optional restriction</div>
                <div *ngIf="readOnly" class="header-note">view only</div>
                <div class="spacer"></div>
                <div class="close" (click)="close()" title="Close">&times;</div>
            </div>

            <div class="step-body">

                <!-- Basics -->
                <div *ngIf="campaign.activeStep == 'basics'" class="config-section basics">
                    <div class="config-inputs">
                        <div class="config-input wide">
                            <div class="label">Campaign Name</div>
                            <input type="text" placeholder="discount name" [(ngModel)]="campaign.name" />
                        </div>
                    </div>
                    <div class="label">Campaign Type</div>
                    <div class="type-options">
                        <div *ngFor="let typeKey of ['salePrice', 'buyOneGetOne', 'buyOneGetOneHalfOff', 'discountedMultiples', 'categoryPercent', 'amountOff']"
                            class="type-option" [class.active]="campaign.subtype == typeKey"
                            (click)="selectSubtype(typeKey)">
                            <div class="name">{{templates[typeKey].name}}</div>
                            <div class="description">{{templates[typeKey].description}}</div>
                        </div>
                    </div>
                </div>

                <!-- Locations -->
                <div *ngIf="campaign.activeStep == 'locations'" class="select-many scope-select">
                    <div class="results">
                        <div class="column-header">Locations</div>
                        <div class="options">
                            <div *ngFor="let option of locations" class="option scope-row"
                                [class.selected]="campaign.locationId?.includes(option._id)"
                                (click)="toggleOption('locationId', option._id)">
                                <span class="check">{{campaign.locationId?.includes(option._id) ? '&#10003;' : '+'}}</span>
                                <span class="name">{{option.name}}</span>
                            </div>
                        </div>
                    </div>
                    <div class="selected">
                        <div class="column-header">
                            Selected<span *ngIf="campaign.locationId?.length">({{campaign.locationId.length}})</span>
                            <div *ngIf="campaign.locationId?.length" class="action" (click)="clearLocations()">Clear</div>
                        </div>
                        <div class="options">
                            <div *ngIf="!campaign.locationId?.length" class="empty-hint">
                                No locations selected &mdash; choose where this campaign runs.
                            </div>
                            <div *ngFor="let id of campaign.locationId" class="option scope-row" (click)="toggleOption('locationId', id)">
                                <span class="check remove">&times;</span>
                                <span class="name">{{locationName(id)}}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Items -->
                <div *ngIf="campaign.activeStep == 'items'" class="select-many scope-select items-select">
                    <div class="filters">
                        <div class="column-header">Find Items</div>
                        <div class="filter-field">
                            <div class="filter-label">Search</div>
                            <div class="search-box">
                                <input type="text" placeholder="Name, UPC, or item #" [(ngModel)]="searchText" (keyup)="updateSearch($event)" />
                            </div>
                        </div>

                        <div class="filter-field" *ngIf="categories">
                            <div class="filter-label">Category</div>
                            <select [(ngModel)]="selectedCategory" (change)="getItems()">
                                <option *ngFor="let category of categories" [value]="category._id">{{category.name}}</option>
                            </select>
                        </div>

                        <div class="filter-field" *ngIf="groups">
                            <div class="filter-label">Group</div>
                            <select [(ngModel)]="selectedGroup" (change)="getItems()">
                                <option *ngFor="let group of groups" [value]="group._id">{{group.name}}</option>
                            </select>
                        </div>

                        <div class="filter-field" *ngIf="unitDescription?.length > 0">
                            <div class="filter-label">Container Size</div>
                            <select [(ngModel)]="selectedUnitDescription" (change)="getItems()">
                                <option *ngFor="let unitDescription of unitDescription" [value]="unitDescription">{{unitDescription}}</option>
                            </select>
                        </div>

                        <div class="filter-field" *ngFor="let field of itemFields">
                            <div class="filter-label">{{field.name}}</div>
                            <select [(ngModel)]="selectedItemFields[field.key]" (change)="getItems()">
                                <option *ngFor="let option of itemFieldOptions[field.key]" [value]="option">{{option}}</option>
                            </select>
                        </div>
                    </div>
                    <div class="results">
                        <div class="column-header">
                            Results<span>({{results?.length}})</span>
                            <div *ngIf="results?.length > 0" class="action" (click)="addAll()">Add All</div>
                        </div>
                        <div class="options">
                            <div *ngIf="!results?.length" class="empty-hint">
                                Search or pick a category, group, or size on the left to list matching items.
                            </div>
                            <div *ngFor="let option of results" class="option scope-row" [class.selected]="option.active" (click)="addOption(option)">
                                <span class="check">{{option.active ? '&#10003;' : '+'}}</span>
                                <span class="name">
                                    {{option.name}}
                                    <span *ngIf="option.unitDescription" class="unit-description">{{option.unitDescription}}</span>
                                    <ng-container *ngFor="let field of itemFields">
                                        <span *ngIf="option[field.key]" class="unit-description">{{option[field.key]}}</span>
                                    </ng-container>
                                    <span *ngIf="option.categoryName" class="category-name">({{option.categoryName}})</span>
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="selected">
                        <div class="column-header">
                            Selected<span>({{campaign.inventoryAccounts?.length}})</span>
                            <div *ngIf="campaign.inventoryAccounts?.length > 0" class="action" (click)="clearSelected()">Clear</div>
                        </div>
                        <div class="options">
                            <div *ngIf="!campaign.inventoryAccounts?.length" class="empty-hint">
                                Nothing selected yet. Use the filters to find items, then click to add them.
                            </div>
                            <div *ngFor="let option of campaign.inventoryAccounts" class="option scope-row" (click)="removeOption(option)">
                                <span class="check remove">&times;</span>
                                <span class="name">
                                    {{option.name}}
                                    <span *ngIf="option.unitDescription" class="unit-description">{{option.unitDescription}}</span>
                                    <ng-container *ngFor="let field of itemFields">
                                        <span *ngIf="option[field.key]" class="unit-description">{{option[field.key]}}</span>
                                    </ng-container>
                                    <span *ngIf="option.categoryName" class="category-name">({{option.categoryName}})</span>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Category / subcategory scope -->
                <div *ngIf="campaign.activeStep == 'categoryScope'" class="select-many scope-select">
                    <div class="results">
                        <div class="column-header">Categories</div>
                        <div class="options">
                            <div *ngFor="let category of scopeCategories" class="option scope-row"
                                [class.active]="activeScopeCategory?._id == category._id"
                                [class.selected]="isScopeSelected(category._id)"
                                (click)="activateScopeCategory(category)">
                                <span class="check" (click)="toggleScopeCategory(category); $event.stopPropagation()">{{isScopeSelected(category._id) ? '&#10003;' : '+'}}</span>
                                <span class="name">{{category.name}}</span>
                                <span *ngIf="category.subcategories?.length" class="sub-count">{{category.subcategories.length}} &#8250;</span>
                            </div>
                        </div>
                    </div>
                    <div class="results">
                        <div class="column-header">
                            Subcategories<span *ngIf="activeScopeCategory">({{activeScopeCategory.name}})</span>
                        </div>
                        <div class="options">
                            <div *ngIf="!activeScopeCategory" class="empty-hint">Select a category on the left to browse its subcategories.</div>
                            <div *ngIf="activeScopeCategory && !activeScopeCategory.subcategories?.length" class="empty-hint">No subcategories in {{activeScopeCategory.name}}.</div>
                            <div *ngIf="activeScopeCategory && isScopeSelected(activeScopeCategory._id)" class="empty-hint">All of {{activeScopeCategory.name}} is selected, subcategories included.</div>
                            <ng-container *ngIf="activeScopeCategory && !isScopeSelected(activeScopeCategory._id)">
                                <div *ngFor="let sub of activeScopeCategory.subcategories" class="option scope-row"
                                    [class.selected]="isScopeSelected(sub._id)"
                                    (click)="toggleScopeSubcategory(sub, activeScopeCategory)">
                                    <span class="check">{{isScopeSelected(sub._id) ? '&#10003;' : '+'}}</span>
                                    <span class="name">{{sub.name}}</span>
                                </div>
                            </ng-container>
                        </div>
                    </div>
                    <div class="selected">
                        <div class="column-header">
                            Selected<span>({{campaign.scope?.length || 0}})</span>
                            <div *ngIf="campaign.scope?.length" class="action" (click)="clearScope()">Clear</div>
                        </div>
                        <div class="options">
                            <div *ngIf="!campaign.scope?.length" class="empty-hint">Nothing selected yet. The discount will apply to items in every category and subcategory you select.</div>
                            <div *ngFor="let entry of campaign.scope" class="option scope-row" (click)="removeScope(entry)">
                                <span class="check remove">&times;</span>
                                <span class="name">{{entry.name}}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Container sizes -->
                <div *ngIf="campaign.activeStep == 'unitDescription'" class="select-many scope-select unit-restriction">
                    <div class="results">
                        <div class="column-header">
                            Container Sizes<span *ngIf="unitSearchText">({{filteredUnitOptions().length}} match)</span>
                        </div>
                        <div class="search-box">
                            <input type="text" placeholder="Search sizes..." [(ngModel)]="unitSearchText" />
                        </div>
                        <div class="options">
                            <div *ngFor="let option of filteredUnitOptions()" class="option scope-row"
                                [class.selected]="campaign.scopeUnitDescription.includes(option)"
                                (click)="toggleUnitDescription(option)">
                                <span class="check">{{campaign.scopeUnitDescription.includes(option) ? '&#10003;' : '+'}}</span>
                                <span class="name">{{option}}</span>
                            </div>
                        </div>
                    </div>
                    <div class="selected">
                        <div class="column-header">
                            Restricted To<span *ngIf="campaign.scopeUnitDescription.length">({{campaign.scopeUnitDescription.length}})</span>
                            <div *ngIf="campaign.scopeUnitDescription.length" class="action" (click)="clearUnitDescriptions()">Clear</div>
                        </div>
                        <div class="options">
                            <div *ngIf="!campaign.scopeUnitDescription.length" class="empty-hint">
                                No size restriction &mdash; every container size in scope is included.
                                Select sizes on the left to limit the discount.
                            </div>
                            <div *ngFor="let value of campaign.scopeUnitDescription" class="option scope-row" (click)="toggleUnitDescription(value)">
                                <span class="check remove">&times;</span>
                                <span class="name">{{value}}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Inventory groups -->
                <div *ngIf="campaign.activeStep == 'inventoryGroup'" class="select-many scope-select">
                    <div class="results">
                        <div class="column-header">Inventory Groups</div>
                        <div class="options">
                            <div *ngFor="let option of inventoryGroupOptions" class="option scope-row"
                                [class.selected]="isEntitySelected('scopeInventoryGroups', option._id)"
                                (click)="toggleEntity('scopeInventoryGroups', option)">
                                <span class="check">{{isEntitySelected('scopeInventoryGroups', option._id) ? '&#10003;' : '+'}}</span>
                                <span class="name">{{option.name}}</span>
                            </div>
                        </div>
                    </div>
                    <div class="selected">
                        <div class="column-header">
                            Restricted To<span *ngIf="campaign.scopeInventoryGroups.length">({{campaign.scopeInventoryGroups.length}})</span>
                            <div *ngIf="campaign.scopeInventoryGroups.length" class="action" (click)="clearEntities('scopeInventoryGroups')">Clear</div>
                        </div>
                        <div class="options">
                            <div *ngIf="!campaign.scopeInventoryGroups.length" class="empty-hint">
                                No group restriction &mdash; items do not need to belong to any inventory group.
                                Select groups on the left to require membership.
                            </div>
                            <div *ngFor="let entry of campaign.scopeInventoryGroups" class="option scope-row" (click)="toggleEntity('scopeInventoryGroups', entry)">
                                <span class="check remove">&times;</span>
                                <span class="name">{{entry.name}}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Excluded inventory groups -->
                <div *ngIf="campaign.activeStep == 'excludedInventoryGroup'" class="select-many scope-select">
                    <div class="results">
                        <div class="column-header">Inventory Groups</div>
                        <div class="options">
                            <div *ngFor="let option of inventoryGroupOptions" class="option scope-row"
                                [class.selected]="isEntitySelected('excludedInventoryGroups', option._id)"
                                (click)="toggleEntity('excludedInventoryGroups', option)">
                                <span class="check">{{isEntitySelected('excludedInventoryGroups', option._id) ? '&#10003;' : '+'}}</span>
                                <span class="name">{{option.name}}</span>
                            </div>
                        </div>
                    </div>
                    <div class="selected">
                        <div class="column-header">
                            Excluded<span *ngIf="campaign.excludedInventoryGroups.length">({{campaign.excludedInventoryGroups.length}})</span>
                            <div *ngIf="campaign.excludedInventoryGroups.length" class="action" (click)="clearEntities('excludedInventoryGroups')">Clear</div>
                        </div>
                        <div class="options">
                            <div *ngIf="!campaign.excludedInventoryGroups.length" class="empty-hint">
                                Nothing excluded &mdash; items in every inventory group can receive the discount.
                                Select groups on the left to keep their items out of this campaign.
                            </div>
                            <div *ngFor="let entry of campaign.excludedInventoryGroups" class="option scope-row" (click)="toggleEntity('excludedInventoryGroups', entry)">
                                <span class="check remove">&times;</span>
                                <span class="name">{{entry.name}}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- User groups -->
                <div *ngIf="campaign.activeStep == 'userGroup'" class="select-many scope-select">
                    <div class="results">
                        <div class="column-header">User Groups</div>
                        <div class="options">
                            <div *ngIf="userGroupError" class="empty-hint">{{userGroupError}}</div>
                            <div *ngIf="!userGroupError && !userGroupOptions?.length" class="empty-hint">
                                No user groups are defined.
                            </div>
                            <div *ngFor="let option of userGroupOptions" class="option scope-row"
                                [class.selected]="isEntitySelected('userGroups', option._id)"
                                (click)="toggleEntity('userGroups', option)">
                                <span class="check">{{isEntitySelected('userGroups', option._id) ? '&#10003;' : '+'}}</span>
                                <span class="name">{{option.name}}</span>
                            </div>
                        </div>
                    </div>
                    <div class="selected">
                        <div class="column-header">
                            Restricted To<span *ngIf="campaign.userGroups.length">({{campaign.userGroups.length}})</span>
                            <div *ngIf="campaign.userGroups.length" class="action" (click)="clearEntities('userGroups')">Clear</div>
                        </div>
                        <div class="options">
                            <div *ngIf="!campaign.userGroups.length" class="empty-hint">
                                No user restriction &mdash; any user's purchases qualify.
                                Select groups to limit the discount to purchases rung up by their members.
                            </div>
                            <div *ngFor="let entry of campaign.userGroups" class="option scope-row" (click)="toggleEntity('userGroups', entry)">
                                <span class="check remove">&times;</span>
                                <span class="name">{{entry.name}}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Customer groups -->
                <div *ngIf="campaign.activeStep == 'customerGroup'" class="select-many scope-select">
                    <div class="results">
                        <div class="column-header">Customer Groups</div>
                        <div class="options">
                            <div *ngIf="customerGroupError" class="empty-hint">{{customerGroupError}}</div>
                            <div *ngIf="!customerGroupError && !customerGroupOptions?.length" class="empty-hint">
                                No customer groups defined yet.</div>
                            <div *ngFor="let option of customerGroupOptions" class="option scope-row"
                                [class.selected]="isEntitySelected('customerGroups', option._id)"
                                (click)="toggleEntity('customerGroups', option)">
                                <span class="check">{{isEntitySelected('customerGroups', option._id) ? '&#10003;' : '+'}}</span>
                                <span class="name">{{option.name}}</span>
                            </div>
                        </div>
                    </div>
                    <div class="selected">
                        <div class="column-header">
                            Restricted To<span *ngIf="campaign.customerGroups.length">({{campaign.customerGroups.length}})</span>
                            <div *ngIf="campaign.customerGroups.length" class="action" (click)="clearEntities('customerGroups')">Clear</div>
                        </div>
                        <div class="options">
                            <div *ngIf="!campaign.customerGroups.length" class="empty-hint">
                                No customer restriction &mdash; the discount applies with or without an attached customer.
                                Select groups to require a customer from those groups on the purchase.
                            </div>
                            <div *ngFor="let entry of campaign.customerGroups" class="option scope-row" (click)="toggleEntity('customerGroups', entry)">
                                <span class="check remove">&times;</span>
                                <span class="name">{{entry.name}}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Per-customer use limit -->
                <div *ngIf="campaign.activeStep == 'customerUseLimit'" class="config-section">
                    <div class="config-inputs">
                        <div class="config-input">
                            <div class="label">{{campaign.customerUsePerItem ? 'Uses Per Product' : 'Uses Per Customer'}}</div>
                            <input type="number" min="1" [(ngModel)]="campaign.maxCustomerUses" />
                            <div class="hint">{{campaign.customerUsePerItem
                                ? 'times each customer may use this discount on each product'
                                : 'completed purchases each customer may use this discount on'}}</div>
                        </div>
                        <div *ngIf="campaign.customerUsePerItem" class="config-input">
                            <div class="label">Maximum Qty Per Use</div>
                            <input type="number" min="1" [(ngModel)]="campaign.maxQtyPerUse" />
                            <div class="hint">units of each product discounted per use &mdash; leave empty for no cap</div>
                        </div>
                    </div>
                    <div class="config-toggles">
                        <div class="checkbox">
                            <input type="checkbox" [(ngModel)]="campaign.customerUsePerItem" />
                            Per-Item
                        </div>
                    </div>
                    <div class="config-note">
                        <ng-container *ngIf="campaign.customerUsePerItem">
                            The limit applies separately to each retail product &mdash; e.g. a customer
                            can get the discount the first time they buy each qualifying item.
                        </ng-container>
                        A customer must be attached to the purchase for the discount to apply &mdash;
                        anonymous purchases never qualify while this limit is set. Usage counts start
                        from when this feature was deployed.
                    </div>
                </div>

                <!-- Minimum buy -->
                <div *ngIf="campaign.activeStep == 'minimumBuy'" class="config-section">
                    <div class="config-inputs">
                        <ng-container [ngSwitch]="campaign.subtype">
                            <div *ngSwitchCase="'buyOneGetOne'" class="config-note">Minimum buy is 2 &mdash; set by the Buy One Get One template.</div>
                            <div *ngSwitchCase="'buyOneGetOneHalfOff'" class="config-note">Minimum buy is 2 &mdash; set by the Buy One Get One template.</div>
                            <ng-container *ngSwitchDefault>
                                <div *ngIf="campaign.subtype == 'discountedMultiples' && campaign.sameItemOnly" class="config-note">
                                    Using per-item minimums &mdash; set each item's Minimum Buy in the Amount table.
                                </div>
                                <div *ngIf="!(campaign.subtype == 'discountedMultiples' && campaign.sameItemOnly)" class="config-input">
                                    <div class="label">Minimum Qty</div>
                                    <input type="number" min="1" [(ngModel)]="campaign.threshold"
                                        (ngModelChange)="campaign.subtype == 'discountedMultiples' ? anyItemFieldUpdated('threshold') : null" />
                                    <div class="hint">qualifying items required in the purchase &mdash; leave empty for no minimum</div>
                                </div>
                            </ng-container>
                        </ng-container>
                    </div>
                </div>

                <!-- Minimum purchase ($ Off) -->
                <div *ngIf="campaign.activeStep == 'minimumPurchase'" class="config-section">
                    <div class="config-inputs">
                        <div class="config-input">
                            <div class="label">Minimum Purchase ($)</div>
                            <input type="number" min="0" step="0.01" [(ngModel)]="campaign.minimumPurchase" />
                            <div class="hint">qualifying items must add up to at least this much before discounts &mdash; leave empty for no minimum</div>
                        </div>
                    </div>
                </div>

                <!-- Amount -->
                <div *ngIf="campaign.activeStep == 'amount'" class="config-section amount-section">
                    <ng-container [ngSwitch]="campaign.subtype">

                        <div *ngSwitchCase="'categoryPercent'" class="config-inputs">
                            <div class="config-input">
                                <div class="label">Discount %</div>
                                <input type="number" min="0" max="100" [(ngModel)]="campaign.discountPercent" />
                                <div class="hint">off every qualifying item once the minimum is met</div>
                            </div>
                        </div>

                        <div *ngSwitchCase="'amountOff'" class="config-inputs">
                            <div class="config-input">
                                <div class="label">Amount Off ($)</div>
                                <input type="number" min="0" step="0.01" [(ngModel)]="campaign.amountOff" />
                                <div class="hint">taken off the purchase once, spread across the qualifying items &mdash; never more than what those items come to</div>
                            </div>
                        </div>

                        <ng-container *ngSwitchCase="'buyOneGetOne'">
                            <div class="config-note">Buy one, get one free. The free item is the lowest-priced qualifying item.</div>
                            <div class="checkbox">
                                <input type="checkbox" [(ngModel)]="campaign.sameItemOnly" />
                                Same Item Only
                            </div>
                        </ng-container>

                        <ng-container *ngSwitchCase="'buyOneGetOneHalfOff'">
                            <div class="config-note">Buy one, get one half off. The discounted item is the lowest-priced qualifying item.</div>
                            <div class="checkbox">
                                <input type="checkbox" [(ngModel)]="campaign.sameItemOnly" />
                                Same Item Only
                            </div>
                        </ng-container>

                        <ng-container *ngSwitchCase="'discountedMultiples'">
                            <div class="config-toggles">
                                <div class="checkbox">
                                    <input type="checkbox" [(ngModel)]="campaign.sameItemOnly" />
                                    Same Item Only
                                </div>
                                <div class="checkbox">
                                    <input type="checkbox" [(ngModel)]="campaign.allAfterThreshold" />
                                    All After Threshold
                                </div>
                            </div>
                            <div *ngIf="!campaign.sameItemOnly" class="config-inputs">
                                <div class="config-input">
                                    <div class="label">Discounted Total Price</div>
                                    <input type="number" [(ngModel)]="campaign.discountedTotalPrice" (ngModelChange)="anyItemFieldUpdated('discountedTotalPrice')" />
                                </div>
                                <div class="config-input">
                                    <div class="label">Discounted Unit Price</div>
                                    <input type="number" [(ngModel)]="campaign.discountedUnitPrice" (ngModelChange)="anyItemFieldUpdated('discountedUnitPrice')" />
                                </div>
                            </div>
                        </ng-container>

                    </ng-container>

                    <div *ngIf="amountHasTable()" class="bulk-fill">
                        <div class="bulk-label">Quick Fill</div>
                        <select [(ngModel)]="bulkColumnKey">
                            <option *ngFor="let column of bulkColumns()" [value]="column.key">{{column.name}}</option>
                        </select>
                        <input type="number" [(ngModel)]="bulkValue" placeholder="value" />
                        <div class="button bulk-apply" (click)="applyBulk(false)">Apply All</div>
                        <div class="button bulk-apply" (click)="applyBulk(true)">Apply Undefined</div>
                        <div class="bulk-hint">Fills the chosen column for every item &mdash; or only items without a value &mdash; and recalculates the rest.</div>
                    </div>

                    <!-- Per-item table (salePrice always; discountedMultiples both variants) -->
                    <div *ngIf="amountHasTable()" class="amount">

                        <div class="item-row item-header">
                            <div *ngFor="let field of amountColumns()" [class]="field.class">{{field.name}}</div>
                            <div class="item-actions"></div>
                        </div>

                        <div class="item-row-wrapper">
                            <div class="item-row" *ngFor="let item of this.campaign.inventoryAccounts; let itemIndex = index;"
                                [class.warn-margin]="hasNegativeMargin(item)"
                                [class.warn-no-discount]="hasZeroDiscount(item)">

                                <div *ngFor="let field of amountColumns()" [class]="field.class">
                                    <ng-container *ngIf="!field.editable">
                                        <div *ngIf="field.path" [class.negative]="item[field.path][field.key] < 0">{{item[field.path][field.key] | rf:field.pipe}}</div>
                                        <div *ngIf="!field.path" [class.negative]="item[field.key] < 0">{{item[field.key] | rf:field.pipe}}</div>
                                    </ng-container>
                                    <ng-container *ngIf="field.editable">
                                        <input
                                            type="text"
                                            id="item-{{itemIndex}}-{{field.key}}"
                                            [(ngModel)]="item.discountSettings[field.key]"
                                            (ngModelChange)="itemFieldUpdated(item, field, $event)"
                                            (keydown.Tab)="fieldTab(field.key, itemIndex);false"
                                            [class.negative]="item[field.path] && item[field.path][field.key] < 0"
                                            />
                                    </ng-container>
                                </div>

                                <div class="item-actions">
                                    <div class="action delete" (click)="removeItem(item)" title="Remove item from campaign">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                            <polyline points="3 6 5 6 21 6"/>
                                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                            <line x1="10" y1="11" x2="10" y2="17"/>
                                            <line x1="14" y1="11" x2="14" y2="17"/>
                                        </svg>
                                    </div>
                                </div>

                            </div>
                        </div>

                    </div>
                </div>

                <!-- Application -->
                <div *ngIf="campaign.activeStep == 'application'" class="config-section">
                    <div class="type-options">
                        <div class="type-option" [class.active]="campaign.applyMode == 'auto'" (click)="campaign.applyMode = 'auto'">
                            <div class="name">Automatic</div>
                            <div class="description">Applies by itself whenever a purchase qualifies</div>
                        </div>
                        <div class="type-option" [class.active]="campaign.applyMode == 'manual'" (click)="campaign.applyMode = 'manual'">
                            <div class="name">Manual</div>
                            <div class="description">The cashier adds it from the purchase Discount list; only qualifying items are discounted</div>
                        </div>
                        <div class="type-option" [class.active]="campaign.applyMode == 'manualItem'" (click)="campaign.applyMode = 'manualItem'">
                            <div class="name">Manual Per Item</div>
                            <div class="description">The cashier adds it to specific line items</div>
                        </div>
                    </div>
                    <div *ngIf="campaign.applyMode != 'auto'" class="config-toggles">
                        <div class="checkbox">
                            <input type="checkbox" [(ngModel)]="campaign.requireNote" />
                            Require Note
                        </div>
                    </div>
                    <div *ngIf="campaign.applyMode != 'auto'" class="config-note">
                        This discount appears in ISE's Discount picker when a purchase qualifies{{campaign.requireNote ? '; the cashier must enter a note when applying it' : ''}}.
                    </div>
                </div>

                <!-- Stacking -->
                <div *ngIf="campaign.activeStep == 'stacking'" class="config-section">
                    <div class="config-toggles">
                        <div class="checkbox">
                            <input type="checkbox" [(ngModel)]="campaign.stackable" />
                            Stackable
                        </div>
                    </div>
                    <div class="config-note">
                        {{campaign.stackable
                            ? 'This discount combines with any other qualifying discounts on the same purchase.'
                            : 'When this and other non-stackable discounts qualify on the same purchase, only the one worth the most applies. Stackable discounts still combine freely.'}}
                    </div>
                </div>

                <!-- Schedule -->
                <div *ngIf="campaign.activeStep == 'schedule'" class="config-section schedule-section">
                    <div class="label">Campaign Window</div>
                    <div class="schedule-facts">
                        <div class="fact">
                            <div class="fact-value">{{scheduleStatus()}}</div>
                            <div class="fact-label">Status</div>
                        </div>
                        <div class="fact">
                            <div class="fact-value">{{scheduleStartLabel()}}</div>
                            <div class="fact-label">Starts</div>
                        </div>
                        <div class="fact">
                            <div class="fact-value">{{scheduleEndLabel()}}</div>
                            <div class="fact-label">Ends</div>
                        </div>
                        <div class="fact">
                            <div class="fact-value">{{scheduleDuration()}}</div>
                            <div class="fact-label">Duration</div>
                        </div>
                    </div>
                    <div class="schedule-columns">
                        <div class="schedule-col">
                            <div class="checkbox">
                                <input type="checkbox" [(ngModel)]="campaign.scheduleStartEnabled" (ngModelChange)="saveUpdates()" />
                                Scheduled Start
                            </div>
                            <div class="hint-line">{{campaign.scheduleStartEnabled ? 'The campaign begins at the selected start date and time.' : 'Starts as soon as the campaign is live.'}}</div>
                        </div>
                        <div class="schedule-col">
                            <div class="checkbox">
                                <input type="checkbox" [(ngModel)]="campaign.scheduleEndEnabled" (ngModelChange)="saveUpdates()" />
                                Scheduled End
                            </div>
                            <div class="hint-line">{{campaign.scheduleEndEnabled ? 'The campaign stops at the selected end date and time.' : 'Runs until the campaign is turned off.'}}</div>
                        </div>
                    </div>
                    <div *ngIf="campaign.scheduleStartEnabled || campaign.scheduleEndEnabled" class="inline-range">
                        <ngx-daterangepicker-material
                            [startDate]="scheduleStart"
                            [endDate]="scheduleEnd"
                            [timePicker]="true"
                            [showDropdowns]="true"
                            [autoApply]="true"
                            [linkedCalendars]="false"
                            (datesUpdated)="inlineRangeUpdated($event)">
                        </ngx-daterangepicker-material>
                    </div>
                    <div class="hint-line">Selections apply immediately and are saved automatically. Only the enabled sides of the range are used.</div>
                </div>

                <!-- Review -->
                <div *ngIf="campaign.activeStep == 'review'" class="review">

                    <div *ngIf="dealHidden" class="deal-hidden-note">
                        <div class="note-title">{{statusLabel()}}</div>
                        <div class="note-text">{{dealHiddenNotice()}}</div>
                    </div>

                    <div *ngIf="!dealHidden" class="review-headline">
                        <div class="headline-text">{{reviewHeadline()}}</div>
                        <div *ngIf="campaign.scope?.length || campaign.subtype == 'categoryPercent' || campaign.subtype == 'amountOff'" class="headline-sub">
                            <ng-container *ngIf="scopeItemCount !== null">Currently matches <b>{{scopeItemCount | number}}</b> items in inventory.</ng-container>
                            <ng-container *ngIf="scopeItemCount === null">Counting matching items&hellip;</ng-container>
                        </div>
                    </div>

                    <div class="review-columns">
                        <div class="review-main">

                            <div class="review-section-title">Configuration</div>
                            <div class="config-cards">
                                <ng-container *ngFor="let key of visibleSections()">
                                    <div *ngIf="key != 'review'" class="config-card" [class.static]="readOnly" (click)="goToStep(key)">
                                        <div class="card-label">{{sections[key].name}}</div>
                                        <div class="card-value" [class.missing]="!readOnly && !isSectionComplete(key) && !isOptionalSection(key)">
                                            {{sectionSummary(key) || (isOptionalSection(key) ? 'No restriction' : (readOnly ? '—' : 'Not set'))}}
                                        </div>
                                        <div *ngIf="!readOnly" class="card-edit">Edit</div>
                                    </div>
                                </ng-container>
                            </div>

                            <ng-container *ngIf="visibleSections().includes('items')">
                                <div class="review-section-title">
                                    {{readOnly ? 'Items' : 'Items Preview'}}
                                    <span *ngIf="!readOnly" class="warn-chips">
                                        <span *ngIf="reviewWarningCounts().belowCost" class="chip chip-yellow">{{reviewWarningCounts().belowCost}} below cost</span>
                                        <span *ngIf="reviewWarningCounts().noop" class="chip chip-orange">{{reviewWarningCounts().noop}} no discount</span>
                                        <span *ngIf="reviewWarningCounts().collisions" class="chip chip-red">{{reviewWarningCounts().collisions}} conflicts</span>
                                    </span>
                                </div>
                                <div class="preview-list">
                                    <div *ngFor="let item of campaign.inventoryAccounts" class="preview-row"
                                        [class.warn-margin]="hasNegativeMargin(item)"
                                        [class.warn-no-discount]="hasZeroDiscount(item)">
                                        <span class="p-name">
                                            {{item.name}}
                                            <span *ngIf="item.unitDescription" class="p-unit">{{item.unitDescription}}</span>
                                        </span>
                                        <span *ngIf="!readOnly && campaign.collisions?.[item._id]?.length > 0" class="chip chip-red"
                                            [title]="collisionNames(item)">conflict</span>
                                        <span class="p-price">
                                            <span class="p-was" [class.p-only]="dealHidden">{{item.price | rf:'currency'}}</span>
                                            <span *ngIf="!dealHidden && previewFinalPrice(item)" class="p-final">{{previewFinalPrice(item)}}</span>
                                        </span>
                                    </div>
                                </div>
                            </ng-container>

                        </div>

                        <div class="publish-panel">
                            <div class="panel-title">{{readOnly ? 'Status' : 'Publish'}}</div>
                            <ng-container *ngIf="!readOnly">
                                <div class="publish-status"
                                    [class.live]="campaign.live"
                                    [class.blocked]="!campaign.live && !readyToGoLive()">
                                    {{campaign.live ? 'Live' : (readyToGoLive() ? 'Ready to publish' : 'Not ready')}}
                                </div>
                                <div *ngIf="!campaign.live && !readyToGoLive()" class="checklist">
                                    <div *ngFor="let key of incompleteSections()" class="check-item" (click)="goToStep(key)">
                                        <span class="dot">&#9679;</span>
                                        <span>{{sections[key].name}}</span>
                                    </div>
                                </div>
                                <div class="live-row">
                                    <span>Campaign Active</span>
                                    <app-toggle (valueChanged)="toggleLive()" [checked]="campaign.live"></app-toggle>
                                </div>
                            </ng-container>
                            <ng-container *ngIf="readOnly">
                                <div class="publish-status" [class.live]="activeNow" [class.blocked]="!activeNow">{{statusLabel()}}</div>
                                <div *ngIf="!activeNow" class="viewer-hint">Tags can be printed once the campaign is active.</div>
                            </ng-container>
                            <div class="panel-actions">
                                <div *ngIf="canPrintTags()" class="button" (click)="printTags()">Print Tags</div>
                                <div *ngIf="canCreateTagList()" class="button" [class.disabled]="creatingTagList" (click)="createTagList()">{{creatingTagList ? 'Creating…' : 'Create Tag List'}}</div>
                                <div *ngIf="!readOnly" class="button" (click)="duplicateCampaign()">Duplicate</div>
                                <div *ngIf="!readOnly" class="button red" (click)="deleteSaleCampaign()">Delete Campaign</div>
                            </div>
                        </div>
                    </div>

                </div>

            </div>

            <!-- PRINT TEMPLATE PICKER -->
            <div *ngIf="printTemplateSelect" class="print-select-wrapper" (mousedown)="cancelPrintTemplateSelect()">
                <div class="print-select" (mousedown)="$event.stopPropagation()">
                    <div class="print-select-header">Print Tags</div>
                    <div class="print-select-body">
                        <div *ngFor="let printTemplate of printTemplates"
                             class="template-option"
                             [class.printing]="printingTemplateKey == printTemplate.key"
                             (click)="printTagsWithTemplate(printTemplate.key)">
                            <div class="template-preview" [style.grid-template-columns]="previewColumns(printTemplate)">
                                <div *ngFor="let cell of previewCells(printTemplate)" class="preview-cell"></div>
                            </div>
                            <div class="template-name">{{printTemplate.name}}</div>
                            <div class="template-description">{{printTemplate.description}}</div>
                        </div>
                    </div>
                    <div class="print-select-footer">
                        <div class="button" (click)="cancelPrintTemplateSelect()">Cancel</div>
                    </div>
                </div>
            </div>

            <div class="step-footer">
                <div *ngIf="campaign.activeStep != visibleSections()[0]" class="button back" (click)="prevStep()">Back</div>
                <div class="stretcher"></div>
                <div *ngIf="campaign.activeStep == 'amount' && amountHasTable()" (click)="reset()" class="button yellow">Discard Changes</div>
                <div *ngIf="campaign.activeStep == 'amount' && amountHasTable()" (click)="saveUpdates()" class="button green save">Save</div>
                <div *ngIf="campaign.activeStep != 'review'" class="button next" (click)="nextStep()">Next</div>
                <div *ngIf="campaign.activeStep == 'review'" class="button green done" (click)="close()">Done</div>
            </div>
        </div>
    </div>
</div>
